<template>
  <div>
    <v-container fluid>
      <v-toolbar color="primary" class="header-title text-white">
        <v-toolbar-title class="title">Tax History</v-toolbar-title>
        <v-spacer />
        <div class="d-flex pr-4">
          <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
        </div>
      </v-toolbar>

      <v-card class="card2">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-history</v-icon>
          Tax Form Submissions
        </v-card-title>

        <v-card-text>
          <!-- Error state: surfaced from the store, with a retry that reloads -->
          <v-alert
            v-if="state.error"
            type="error"
            variant="tonal"
            class="mb-4"
            density="comfortable"
          >
            <div class="d-flex align-center">
              <span>{{ state.error }}</span>
              <v-spacer />
              <v-btn color="error" variant="text" size="small" @click="reload">
                ลองใหม่
              </v-btn>
            </div>
          </v-alert>

          <!-- Filters row -->
          <v-row align="center" class="mb-1" no-gutters>
            <v-col cols="12" sm="3" class="pa-2">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                label="Status"
                variant="outlined"
                density="compact"
                hide-details
                data-testid="history-status-filter"
                @update:model-value="applyFilters"
              />
            </v-col>
            <v-col cols="12" sm="3" class="pa-2">
              <v-select
                v-model="yearFilter"
                :items="yearOptions"
                item-title="title"
                item-value="value"
                label="Tax Year"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                data-testid="history-year-filter"
                @update:model-value="applyFilters"
              />
            </v-col>
            <v-spacer />
            <v-col cols="12" sm="4" class="pa-2">
              <v-text-field
                v-model="search"
                data-testid="history-search"
                prepend-inner-icon="mdi-magnify"
                label="Search CIF / Session"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
            </v-col>
          </v-row>

          <div class="table-wrapper">
            <v-data-table-server
              data-testid="history-forms-table"
              class="my-data-table"
              density="comfortable"
              hover
              :headers="headers"
              :items="displayForms"
              :items-length="state.totalCount"
              :items-per-page="state.pageSize"
              :page="state.page"
              :loading="state.loading"
              @update:options="onOptions"
            >
              <!-- Running number based on the current server page -->
              <template #item.index="{ index }">
                {{ (state.page - 1) * state.pageSize + index + 1 }}
              </template>

              <!-- IDs: monospace + copy + truncate, matching the detail view -->
              <template #item.userID="{ value }">
                <CopyId :value="value" label="CIF" truncate />
              </template>

              <template #item.sessionID="{ value }">
                <CopyId :value="value" label="Session" truncate />
              </template>

              <template #item.status="{ value }">
                <v-chip :color="statusColor(value)" size="small" variant="tonal" label>
                  {{ value }}
                </v-chip>
              </template>

              <template #item.isJointFiling="{ value }">
                <v-chip
                  v-if="value"
                  color="info"
                  size="small"
                  variant="tonal"
                  label
                  prepend-icon="mdi-account-multiple"
                >
                  ยื่นร่วม
                </v-chip>
                <span v-else class="text-disabled">—</span>
              </template>

              <template #item.startedAt="{ value }">
                {{ formatDate(value) }}
              </template>

              <template #item.completedAt="{ value }">
                {{ formatDate(value) }}
              </template>

              <template #item.actions="{ item }">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  :data-testid="`history-view-${item.formID}`"
                  @click="viewForm(item)"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </template>

              <!-- Empty state -->
              <template #no-data>
                <div
                  v-if="!state.loading && state.totalCount === 0"
                  class="empty-state"
                >
                  ยังไม่มีฟอร์มภาษี
                </div>
                <div v-else class="empty-state">ไม่พบรายการที่ค้นหา</div>
              </template>
            </v-data-table-server>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { state, loadForms, formatDate } from '../utils/taxHistoryStore.js';
import CopyId from '../components/CopyId.vue';

export default {
  name: 'TaxHistory',
  components: { CopyId },
  data() {
    return {
      today: new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }),
      search: '',
      statusFilter: null,
      yearFilter: null,
      // One-shot guard: v-data-table-server emits update:options once on mount;
      // created() owns the initial load, so we swallow that very first echo.
      optionsReady: false,
      statusOptions: [
        { title: 'All', value: null },
        { title: 'InProgress', value: 'InProgress' },
        { title: 'Completed', value: 'Completed' },
        { title: 'Abandoned', value: 'Abandoned' },
      ],
      headers: [
        { title: 'No.', key: 'index', sortable: false, width: 56 },
        { title: 'CIF', key: 'userID', width: 220 },
        { title: 'Session', key: 'sessionID', width: 180 },
        { title: 'Tax Year', key: 'taxYearCode' },
        { title: 'Status', key: 'status' },
        { title: 'ยื่นร่วม', key: 'isJointFiling', align: 'center', sortable: false, width: 96 },
        { title: 'Started', key: 'startedAt' },
        { title: 'Completed', key: 'completedAt' },
        { title: 'Action', key: 'actions', sortable: false, width: 80, align: 'center' },
      ],
    };
  },
  computed: {
    // Expose the reactive store state to the template.
    state() {
      return state;
    },
    // Tax Year options = page-local distinct of the currently-loaded forms.
    // MVP limitation (per spec S1-P6): years outside the current page (pageSize 50)
    // won't appear. Dedupe by yearID, drop null years.
    yearOptions() {
      const seen = new Set();
      const opts = [];
      for (const f of state.forms) {
        if (f.yearID == null || seen.has(f.yearID)) continue;
        seen.add(f.yearID);
        opts.push({ title: f.taxYearCode || ('Year ' + f.yearID), value: f.yearID });
      }
      return opts;
    },
    // Client-side search WITHIN the current page only (no server full-text):
    // matches userID OR sessionID, case-insensitive.
    displayForms() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return state.forms;
      return state.forms.filter(
        (f) =>
          (f.userID && String(f.userID).toLowerCase().includes(q)) ||
          (f.sessionID && String(f.sessionID).toLowerCase().includes(q))
      );
    },
  },
  created() {
    // Initial load (page 1). The table's mount echo is swallowed by onOptions.
    loadForms({ page: 1, pageSize: state.pageSize }).catch(() => {});
  },
  methods: {
    // Imported helper must be exposed on the instance so the template (_ctx)
    // can call it — Options API does not surface module-scope imports to the
    // template (TaxHistoryDetail.vue registers it the same way).
    formatDate,
    statusColor(status) {
      if (status === 'InProgress') return 'warning'; // amber
      if (status === 'Completed') return 'success'; // green
      if (status === 'Abandoned') return 'grey';
      return 'grey';
    },
    // Single fetch helper: always carries the current filters.
    fetch(page, pageSize) {
      loadForms({
        page,
        pageSize,
        status: this.statusFilter || undefined,
        yearId: this.yearFilter != null ? this.yearFilter : undefined,
      }).catch(() => {
        /* store already set state.error; swallow the rethrow */
      });
    },
    onOptions(opts) {
      // Skip the table's initial update:options echo (created() did the first load).
      // Every later emit (page / items-per-page change) drives a real reload.
      if (!this.optionsReady) {
        this.optionsReady = true;
        return;
      }
      this.fetch(opts.page, opts.itemsPerPage);
    },
    // Status / Tax Year change → reload from page 1 with the new filter.
    applyFilters() {
      this.fetch(1, state.pageSize);
    },
    // Retry button (error state).
    reload() {
      this.fetch(state.page || 1, state.pageSize);
    },
    viewForm(item) {
      this.$router.push('/TaxHistory/' + item.formID);
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  max-height: 600px;
  overflow: auto;
}
.empty-state {
  padding: 32px 16px;
  text-align: center;
  color: #888;
}
</style>
